import { Box, Tab, Tabs, Typography } from "@mui/material"
import { ChevronRightOutlined, FilterAltOutlined } from "@mui-symbols-material/w300"
import type { FC } from "react"
import { useMemo, useState } from "react"

import { OPPORTUNITY_PIPELINE_TAB } from "../constants"
import { useGetSalesOpportunityCompaniesByIds } from "../hooks/useGetSalesOpportunityCompaniesByIds"
import { useSalesOpportunityList } from "../hooks/useSalesOpportunityList"

import { NewRequestDialog } from "./NewRequestDialog/NewRequestDialog"
import { SalesOpportunityFilterChips } from "./SalesOpportunityListPage/SalesOpportunityFilterChips"
import { SalesOpportunityFilterDrawer } from "./SalesOpportunityListPage/SalesOpportunityFilterDrawer"
import { SalesOpportunityTable } from "./SalesOpportunityListPage/SalesOpportunityTable"

import { Button } from "@/components/common/Button"
import SearchInput from "@/components/SearchInput/SearchInput"
import useGetUsers from "@/screens/ChangeLog/hooks/useGetUsers"

export const SalesPage: FC = () => {
  const [isNewRequestOpen, setIsNewRequestOpen] = useState(false)
  const [isFilterDrawerOpen, setIsFilterDrawerOpen] = useState(false)

  const {
    salesOpportunities,
    isFetching,
    error,
    sorting,
    expandedState,
    searchParams,
    tableContainerRef,
    refetch,
    handleSortingChange,
    handleSearchChange,
    handleFilterChange,
    handleFilterRemove,
    handleFilterClear,
    handleScroll,
    onExpandedChange,
  } = useSalesOpportunityList({ route: "/app/sales/" })

  const handleNewRequestOpen = () => setIsNewRequestOpen(true)
  const handleNewRequestClose = () => setIsNewRequestOpen(false)
  const handleNewRequestSubmit = () => {
    setIsNewRequestOpen(false)
    onExpandedChange({})
    refetch()
  }

  const handleFilterDrawerToggle = () => setIsFilterDrawerOpen((prev) => !prev)

  const { companies } = useGetSalesOpportunityCompaniesByIds({ companyIds: searchParams.companyIds })
  const customerNameById = useMemo(() => {
    const map: Record<string, string> = {}
    companies.forEach((company) => {
      if (company.companyId && company.name) map[company.companyId] = company.name
    })
    return map
  }, [companies])

  const { userList } = useGetUsers({
    userIds: searchParams.createdByUserIds,
    pause: !searchParams.createdByUserIds?.length,
  })
  const userNameById = useMemo(() => {
    const map: Record<string, string> = {}
    userList.forEach((user) => {
      if (user.userId && user.name) map[user.userId] = user.name
    })
    return map
  }, [userList])

  return (
    <Box className='flex h-full flex-col gap-y-4'>
      <Typography variant='h5' className='font-normal text-gray-900'>
        Sales Pipeline
      </Typography>

      <Box>
        <Tabs value={OPPORTUNITY_PIPELINE_TAB}>
          <Tab label='Opportunity Pipeline' value={OPPORTUNITY_PIPELINE_TAB} />
        </Tabs>
      </Box>

      <Box className='flex items-center gap-x-3'>
        <SearchInput
          placeholder='Search by Company Name, ID'
          className='w-full max-w-[400px]'
          defaultValue={searchParams.query ?? ""}
          onChange={(e) => handleSearchChange(e.target.value)}
          data-testid='sales.page.search-field'
        />
        <Button
          appearance='outlined'
          startIcon={<FilterAltOutlined />}
          onClick={handleFilterDrawerToggle}
          data-testid='sales.page.filter-button'
        >
          Filter
        </Button>
        <Box className='flex-1' />
        <Button
          appearance='contained'
          endIcon={<ChevronRightOutlined />}
          onClick={handleNewRequestOpen}
          data-testid='sales.page.start-request-button'
        >
          Start Request
        </Button>
      </Box>

      <SalesOpportunityFilterChips
        searchParams={searchParams}
        customerNameById={customerNameById}
        userNameById={userNameById}
        onRemove={handleFilterRemove}
      />

      <Box className='flex min-h-0 flex-1 overflow-y-auto'>
        <SalesOpportunityTable
          data={salesOpportunities}
          loading={isFetching}
          error={error}
          sorting={sorting}
          expandedState={expandedState}
          containerRef={tableContainerRef}
          onSortingChange={handleSortingChange}
          onExpandedChange={onExpandedChange}
          onScroll={handleScroll}
        />
      </Box>

      <SalesOpportunityFilterDrawer
        open={isFilterDrawerOpen}
        searchParams={searchParams}
        onToggle={handleFilterDrawerToggle}
        onChange={handleFilterChange}
        onClear={handleFilterClear}
      />

      {isNewRequestOpen && <NewRequestDialog onClose={handleNewRequestClose} onSubmit={handleNewRequestSubmit} />}
    </Box>
  )
}
